/**
 * Overlays: drawer, dialog and the confirmation pattern.
 *
 * These are the only surfaces in the system that lift off the page (DESIGN_SYSTEM §2.6) — panels never do.
 *
 * The confirmation dialog deserves a note. A consequential action in this ERP does not just ask "are you
 * sure": it states what will happen, and where the brief requires a reason (corrections, reversals,
 * overrides, adjustments, every CRITICAL command) it collects one and will not proceed without it. The
 * reason is not decoration — it travels to the command function, into the audit trail, and is what someone
 * reads six months later when they ask why a number changed.
 */
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button, cx } from './primitives';

// The open overlays, newest last: Escape closes only the one on top, so a confirmation over a drawer
// closes alone and the drawer stays (D19 phase 19).
const openStack: symbol[] = [];

function useDismissOnEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const me = Symbol('overlay');
    openStack.push(me);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openStack[openStack.length - 1] === me) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      openStack.splice(openStack.indexOf(me), 1);
    };
  }, [open, onClose]);
}

/**
 * Forms that open where they are (the owner, 2026-10-07: "forms should be opened there only"). Inside <InlineOverlays>,
 * a Drawer or a Dialog does not lift off the page: it opens as a panel in its place, scrolled into view, with the same
 * title, close button, body and footer. Escape still closes it. Outside it, nothing changes.
 */
const InlineOverlayContext = createContext(false);
export function InlineOverlays({ children }: { children: ReactNode }) {
  return <InlineOverlayContext.Provider value={true}>{children}</InlineOverlayContext.Provider>;
}
export function useInlineOverlays() { return useContext(InlineOverlayContext); }

function InlinePanel({ title, onClose, children, footer, footerClass }: {
  title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; footerClass: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  useDismissOnEscape(true, onClose);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    ref.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
  }, []);
  return (
    <section ref={ref} role="region" aria-labelledby={titleId}
      className="inline-form my-3 overflow-hidden rounded-[var(--radius-panel)] border-2 border-brand/40 bg-surface shadow-[var(--shadow-overlay)]">
      <header className="flex items-center justify-between gap-3 border-b border-line bg-brand-soft px-4 py-2.5">
        <h2 id={titleId} className="text-section">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close"
          className="rounded-[var(--radius-control)] p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink"><X size={15} /></button>
      </header>
      <div className="px-4 py-4">{children}</div>
      {footer ? <footer className={footerClass}>{footer}</footer> : null}
    </section>
  );
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focus moves into the overlay, Tab and Shift+Tab go round inside it, and focus returns where it came from
 *  on close, which keyboard users depend on. */
function useFocusTrap(open: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? ref.current)?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !ref.current) return;
      const items = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (items.length === 0) { event.preventDefault(); ref.current.focus(); return; }
      const head = items[0]!, tail = items[items.length - 1]!;
      const inside = ref.current.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === head || !inside)) { event.preventDefault(); tail.focus(); }
      else if (!event.shiftKey && (document.activeElement === tail || !inside)) { event.preventDefault(); head.focus(); }
    };
    const box = ref.current;
    box?.addEventListener('keydown', onKey);
    return () => { box?.removeEventListener('keydown', onKey); previous?.focus(); };
  }, [open]);
  return ref;
}

export function Drawer({
  open, onClose, title, children, footer, width = 'var(--width-rail)',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const inline = useInlineOverlays();
  useDismissOnEscape(open && !inline, onClose);
  const ref = useFocusTrap(open && !inline);
  const titleId = useId();

  if (!open) return null;
  if (inline) return <InlinePanel title={title} onClose={onClose} footer={footer} footerClass="border-t border-line px-4 py-3">{children}</InlinePanel>;

  // Into document.body: inside a glass panel (backdrop-filter) a fixed overlay would size itself to the page, not the screen.
  return createPortal(
    <div className="overlay-scrim fixed inset-0 z-40 flex justify-end bg-ink/20" onMouseDown={onClose} role="presentation">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ width }}
        onMouseDown={(event) => event.stopPropagation()}
        className="overlay-surface overlay-drawer flex h-full max-w-full flex-col border-l border-line bg-surface shadow-[var(--shadow-overlay)]"
      >
        <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-line px-4">
          <h2 id={titleId} className="truncate text-section">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-[var(--radius-control)] p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            <X size={15} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? <footer className="shrink-0 border-t border-line px-4 py-3">{footer}</footer> : null}
      </div>
    </div>,
    document.body,

  );
}

export function Dialog({
  open, onClose, title, children, footer, width = '32rem', height, flush = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  /** A fixed height (the Exception center's square change form); by default the content sets it. */
  height?: string;
  /** No padding around the body and a full-width footer: for a form that lays itself out. */
  flush?: boolean;
}) {
  const inline = useInlineOverlays();
  useDismissOnEscape(open && !inline, onClose);
  const ref = useFocusTrap(open && !inline);
  const titleId = useId();

  if (!open) return null;
  if (inline) return <InlinePanel title={title} onClose={onClose} footer={footer} footerClass="flex justify-end gap-2 border-t border-line px-4 py-3">{children}</InlinePanel>;

  // Into document.body: inside a glass panel (backdrop-filter) a fixed overlay would size itself to the page, not the screen.
  return createPortal(
    <div
      className="overlay-scrim fixed inset-0 z-50 flex items-center justify-center bg-ink/25 p-6"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ width, height, maxWidth: '100%' }}
        onMouseDown={(event) => event.stopPropagation()}
        className="overlay-surface flex max-h-full flex-col overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-overlay)]"
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 id={titleId} className="text-section">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-[var(--radius-control)] p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            <X size={15} />
          </button>
        </header>
        <div className={cx('min-h-0 flex-1 overflow-y-auto', !flush && 'px-4 py-4')}>{children}</div>
        {footer ? (
          <footer className={cx('shrink-0 border-t border-line px-4 py-3', !flush && 'flex justify-end gap-2')}>{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,

  );
}

export interface ConfirmProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** What will actually happen, in plain words. Not "are you sure?". */
  consequence: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  /**
   * When true the action cannot proceed without a reason, and the reason travels to the command function
   * and into the audit trail (BRIEF §21). Required for corrections, reversals, overrides, adjustments and
   * every CRITICAL command.
   */
  requireReason?: boolean;
  onConfirm: (reason: string | undefined) => void | Promise<void>;
}

export function ConfirmDialog({
  open, onClose, title, consequence, confirmLabel = 'Confirm', destructive, requireReason, onConfirm,
}: ConfirmProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reasonId = useId();

  useEffect(() => {
    if (open) {
      setReason('');
      setBusy(false);
    }
  }, [open]);

  // The database applies the same minimum, so a shorter reason would be refused there anyway.
  const reasonIsValid = !requireReason || reason.trim().length >= 3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={!reasonIsValid || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(requireReason ? reason.trim() : undefined);
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-body">{consequence}</p>

      {requireReason ? (
        <div className="mt-4">
          <label htmlFor={reasonId} className="label-text">
            Reason (recorded in the audit trail)
          </label>
          <textarea
            id={reasonId}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            className={cx(
              'mt-1 w-full rounded-[var(--radius-control)] border bg-surface px-2 py-1.5 text-body',
              'border-line-strong focus:border-brand',
            )}
            placeholder="Why is this change being made?"
          />
          {!reasonIsValid && reason.length > 0 ? (
            <p className="mt-1 text-small text-refused">A reason of at least three characters is required.</p>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
